import { useState, Fragment } from 'react';
import { Icon } from '../utils/icons';

const GeographicDataList = ({ geographicData, isLoading }) => {
    const [sortBy, setSortBy] = useState('views'); // 'views' or 'visitors'
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
    const [expandedCountry, setExpandedCountry] = useState(null);

    // Sort the data based on selected criteria
    const sortedData = [...(geographicData || [])].sort((a, b) => {
        let aValue, bValue;

        if (sortBy === 'views') {
            aValue = a.views || 0;
            bValue = b.views || 0;
        } else if (sortBy === 'visitors') {
            aValue = a.uniqueVisitors || 0;
            bValue = b.uniqueVisitors || 0;
        } else if (sortBy === 'country') {
            aValue = (a.country || a._id || '').toLowerCase();
            bValue = (b.country || b._id || '').toLowerCase();
            // For string comparison, handle differently
            if (sortOrder === 'desc') {
                return bValue.localeCompare(aValue);
            } else {
                return aValue.localeCompare(bValue);
            }
        }

        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    });

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    const handleSort = (newSortBy) => {
        if (sortBy === newSortBy) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(newSortBy);
            setSortOrder('desc');
        }
    };

    const SortIcon = ({ field }) => {
        if (sortBy !== field) {
            return (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
            );
        }

        return sortOrder === 'desc' ? (
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
        ) : (
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
        );
    };

    const getEngagementRate = (views, visitors) => {
        if (!views || !visitors) return 0;
        return ((visitors / views) * 100).toFixed(1);
    };

    const getCountryFlag = (countryCode) => {
        if (!countryCode || countryCode.length !== 2) return '🌍';

        try {
            // Convert country code to flag emoji
            const codePoints = countryCode
                .toUpperCase()
                .split('')
                .map(char => 127397 + char.charCodeAt(0));
            return String.fromCodePoint(...codePoints);
        } catch (error) {
            return '🌍';
        }
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Geographic Traffic</h3>
                <div className="space-y-3">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="flex items-center justify-between animate-pulse">
                            <div className="flex items-center space-x-3">
                                <div className="w-6 h-4 bg-gray-200 rounded"></div>
                                <div className="w-24 h-4 bg-gray-200 rounded"></div>
                            </div>
                            <div className="w-20 h-4 bg-gray-200 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Geographic Traffic</h3>
                <div className="text-xs text-gray-500">
                    {sortedData.length} {sortedData.length === 1 ? 'country' : 'countries'}
                </div>
            </div>

            {/* Sort Controls */}
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <span className="text-xs text-gray-500 font-medium">Sort by:</span>
                <button
                    onClick={() => handleSort('country')}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${sortBy === 'country'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                >
                    Country
                    <SortIcon field="country" />
                </button>
                <button
                    onClick={() => handleSort('views')}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${sortBy === 'views'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                >
                    Views
                    <SortIcon field="views" />
                </button>
                <button
                    onClick={() => handleSort('visitors')}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${sortBy === 'visitors'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                >
                    Visitors
                    <SortIcon field="visitors" />
                </button>
            </div>

            {/* Data List with Fixed Height and Scroll */}
            <div className="relative">
                {sortedData.length > 0 ? (
                    <div
                        className="space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2"
                        style={{
                            height: '320px',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#cbd5e1 #f1f5f9'
                        }}
                    >
                        {sortedData.map((location, index) => {
                            const countryName = location.country || location._id || 'Unknown';
                            const views = location.views || 0;
                            const visitors = location.uniqueVisitors || 0;
                            const engagementRate = getEngagementRate(views, visitors);

                            const isExpanded = expandedCountry === index;
                            const validCities = location.cities?.filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown') || [];
                            const validBrowsers = location.browserTypes?.filter(browser => browser && browser !== 'Unknown' && browser.toLowerCase() !== 'unknown') || [];
                            const hasAdditionalData = validCities.length > 0 ||
                                location.deviceTypes?.length > 0 ||
                                validBrowsers.length > 0 ||
                                location.firstVisit ||
                                location.lastVisit;

                            return (
                                <Fragment key={index}>
                                    <div
                                        className="flex items-center justify-between py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors group border border-transparent hover:border-gray-200"
                                    >
                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                            {/* Flag and Country */}
                                            <div className="flex items-center space-x-2">
                                                <span className="text-lg">{getCountryFlag(location.countryCode)}</span>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900 truncate">
                                                        {countryName}
                                                    </div>
                                                    <div className="text-xs text-gray-500 space-y-0.5">
                                                        <div>{location.countryCode || '??'}</div>
                                                        {(location.cityCount > 0 || location.cities?.length > 0) && (
                                                            <div className="flex items-center text-xs text-blue-600">
                                                                <Icon name="location" size={12} className="mr-1" />
                                                                {location.cities?.filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown').length > 0 ?
                                                                    location.cities.filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown').slice(0, 2).join(', ') +
                                                                    (location.cities.filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown').length > 2 ? ` +${location.cities.filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown').length - 2}` : '') :
                                                                    `${location.cityCount || 0} cities`
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className="flex-1 max-w-32">
                                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                    <div
                                                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                        style={{
                                                            width: `${Math.min((views / (sortedData[0]?.views || 1)) * 100, 100)}%`
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div className="flex items-center space-x-3 text-right">
                                            <div>
                                                <div className="text-sm font-semibold text-gray-900">
                                                    {formatNumber(views)}
                                                </div>
                                                <div className="text-xs text-gray-500">views</div>
                                            </div>
                                            <div>
                                                <div className="text-sm font-semibold text-blue-600">
                                                    {formatNumber(visitors)}
                                                </div>
                                                <div className="text-xs text-gray-500">unique</div>
                                            </div>
                                            {location.avgLoadTime && (
                                                <div className="min-w-12">
                                                    <div className="text-xs font-medium text-purple-600">
                                                        {location.avgLoadTime < 1000
                                                            ? `${Math.round(location.avgLoadTime)}ms`
                                                            : `${(location.avgLoadTime / 1000).toFixed(1)}s`
                                                        }
                                                    </div>
                                                    <div className="text-xs text-gray-400">load</div>
                                                </div>
                                            )}
                                            <div className="min-w-12">
                                                <div className="text-xs font-medium text-green-600">
                                                    {engagementRate}%
                                                </div>
                                                <div className="text-xs text-gray-400">rate</div>
                                            </div>
                                            {hasAdditionalData && (
                                                <button
                                                    onClick={() => setExpandedCountry(isExpanded ? null : index)}
                                                    className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    <svg
                                                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    {isExpanded && hasAdditionalData && (
                                        <div className="px-6 py-3 bg-gray-50 border-l-2 border-blue-200 ml-6 mr-3 rounded-lg mb-2">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                                {/* Cities */}
                                                {location.cities?.filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown').length > 0 && (
                                                    <div>
                                                        <div className="font-medium text-gray-700 mb-1">
                                                            Cities ({location.cities.filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown').length})
                                                        </div>
                                                        <div className="space-y-1 max-h-20 overflow-y-auto">
                                                            {location.cities
                                                                .filter(city => city && city !== 'Unknown' && city.toLowerCase() !== 'unknown')
                                                                .map((city, idx) => (
                                                                    <div key={idx} className="flex items-center text-gray-600">
                                                                        <Icon name="location" size={14} className="mr-2 text-blue-500" />
                                                                        {city}
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Device Types */}
                                                {location.deviceTypes?.length > 0 && (
                                                    <div>
                                                        <div className="font-medium text-gray-700 mb-1">Device Types</div>
                                                        <div className="space-y-1">
                                                            {location.deviceTypes.filter(Boolean).map((device, idx) => (
                                                                <div key={idx} className="flex items-center text-gray-600 capitalize">
                                                                    <Icon name={device === 'mobile' ? 'phone' : device === 'tablet' ? 'tablet' : 'computer'}
                                                                        size={14}
                                                                        className="mr-2 text-green-500" />
                                                                    {device}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Browser Types */}
                                                {validBrowsers.length > 0 && (
                                                    <div>
                                                        <div className="font-medium text-gray-700 mb-1">Browsers</div>
                                                        <div className="space-y-1">
                                                            {validBrowsers
                                                                .slice(0, 4)
                                                                .map((browser, idx) => (
                                                                    <div key={idx} className="flex items-center text-gray-600">
                                                                        <Icon name="language" size={14} className="mr-2 text-purple-500" />
                                                                        {browser}
                                                                    </div>
                                                                ))
                                                            }
                                                            {validBrowsers.length > 4 && (
                                                                <div className="text-gray-500 ml-6">
                                                                    +{validBrowsers.length - 4} more
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Visit Dates */}
                                                {(location.firstVisit || location.lastVisit) && (
                                                    <div className="md:col-span-2">
                                                        <div className="font-medium text-gray-700 mb-1">Visit Activity</div>
                                                        <div className="flex space-x-4 text-gray-600">
                                                            {location.firstVisit && (
                                                                <div className="flex items-center">
                                                                    <Icon name="schedule" size={14} className="mr-2 text-orange-500" />
                                                                    First: {new Date(location.firstVisit).toLocaleDateString()}
                                                                </div>
                                                            )}
                                                            {location.lastVisit && (
                                                                <div className="flex items-center">
                                                                    <Icon name="time" size={14} className="mr-2 text-indigo-500" />
                                                                    Last: {new Date(location.lastVisit).toLocaleDateString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </Fragment>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                            </svg>
                        </div>
                        <div className="text-sm font-medium text-gray-600 mb-1">No geographic data</div>
                        <div className="text-xs text-gray-400">Widget views from different countries will appear here</div>
                    </div>
                )}
            </div>

            {/* Summary Stats at Bottom */}
            {sortedData.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Total countries: {sortedData.length}</span>
                        <span>
                            Total traffic: {formatNumber(sortedData.reduce((sum, item) => sum + (item.views || 0), 0))} views
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GeographicDataList;
